import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

/**
 * Two compact ~88px doughnuts (Audience Mix + Upload Mix) side-by-side.
 * Reused on the KOL profile and the List detail page — the caller supplies
 * pre-computed segment arrays, which keeps this component data-source-agnostic.
 *
 * Segment shape:  { key, name, value, color, pct }
 *
 * Also exports `computeAudienceMix()` and `computeUploadMix()` helpers so
 * both call sites derive segments the same way.
 */

const BIAS_META = {
  crypto: { label: "Crypto", color: "#00F0FF" },
  forex:  { label: "Forex",  color: "#A78BFA" },
  nifty:  { label: "TradFi",  color: "#FBBF24" },
  live:   { label: "Livestream", color: "#FBBF24" },
  video:  { label: "Standard",   color: "#00F0FF" },
  // Round-9 Item 2: Shorts is a third upload-mix part. Pre-R9 shorts
  // were lumped into Standard, which is why the donut only ever showed
  // Standard + Livestream.
  shorts: { label: "Shorts",     color: "#FF4D8D" },
};

// --- helpers ------------------------------------------------------------
function _pct(part, total) {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

/**
 * Aggregate audience mix from a list of counts keyed by category name.
 * `counts` = { crypto, forex, nifty } — pass raw video counts per category.
 */
export function computeAudienceMix({ crypto = 0, forex = 0, nifty = 0 } = {}) {
  const total = crypto + forex + nifty;
  const segments = [
    { key: "crypto", raw: crypto },
    { key: "forex",  raw: forex  },
    { key: "nifty",  raw: nifty  },
  ]
    .filter((s) => s.raw > 0)
    .map((s) => ({
      key: s.key,
      name: BIAS_META[s.key].label,
      value: s.raw,
      color: BIAS_META[s.key].color,
      pct: _pct(s.raw, total),
    }));
  return { total, segments };
}

/**
 * Aggregate upload mix — `live`, `normal`, and `shorts` are absolute
 * video counts. The three parts sum to the creator's classified
 * promo-video total (Round-9 Item 2). Omit `shorts` to get the pre-R9
 * two-part donut (back-compat for the List detail card which still
 * ships without shorts).
 */
export function computeUploadMix({ live = 0, normal = 0, shorts = 0 } = {}) {
  const total = live + normal + shorts;
  const segments = [];
  if (shorts > 0) segments.push({ key: "shorts", name: BIAS_META.shorts.label, value: shorts, color: BIAS_META.shorts.color, pct: _pct(shorts, total) });
  if (live > 0)   segments.push({ key: "live",  name: BIAS_META.live.label,  value: live,   color: BIAS_META.live.color,  pct: _pct(live, total) });
  if (normal > 0) segments.push({ key: "video", name: BIAS_META.video.label, value: normal, color: BIAS_META.video.color, pct: _pct(normal, total) });
  return { total, segments };
}

// --- component ----------------------------------------------------------
export default function BiasDonutsCard({ audience, upload, audienceAccent = null, uploadAccent = null, className = "", extra = null }) {
  if ((audience?.total || 0) === 0 && (upload?.total || 0) === 0) return null;
  return (
    <div
      data-testid="bias-donuts-card"
      className={`border hairline rounded-md px-5 py-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-4 ${className}`}
      style={{ background: "var(--surface)" }}
    >
      <MiniDonut testid="donut-audience-bias" title="Audience Mix" total={audience?.total || 0} data={audience?.segments || []} accent={audienceAccent} />
      <div className="hidden md:block w-px h-16" style={{ background: "rgba(255,255,255,0.06)" }} />
      <MiniDonut testid="donut-upload-bias"  title="Upload Mix"   total={upload?.total || 0}   data={upload?.segments || []}   accent={uploadAccent} />
      {extra}
    </div>
  );
}

function MiniDonut({ testid, title, total, data, accent }) {
  // Centre label must reflect the MAJORITY segment, not simply the first
  // one in the array. Otherwise a creator who is 89% Standard / 11%
  // Livestream reads as "Livestream" in the middle just because the
  // computeUploadMix helper happens to push `live` first.
  const primary = data.length
    ? data.reduce((best, d) => (d.value > (best?.value || 0) ? d : best), null)
    : null;
  return (
    <div data-testid={testid} className="flex items-center gap-4">
      <div style={{ width: 88, height: 88, position: "relative" }}>
        {total > 0 ? (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={28}
                outerRadius={42}
                stroke="none"
                paddingAngle={2}
                isAnimationActive={false}
              >
                {data.map((d) => (<Cell key={d.key} fill={d.color} />))}
              </Pie>
              <Tooltip
                formatter={(v, n) => [`${v} (${data.find((x) => x.name === n)?.pct || 0}%)`, n]}
                contentStyle={{ background: "#121214", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, fontSize: 12, padding: "6px 8px" }}
                labelStyle={{ display: "none" }}
                itemStyle={{ color: "#F8FAFC" }}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-[10px] text-zinc-600">—</div>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="mono text-sm font-semibold text-white leading-none">
            {primary ? `${primary.pct}%` : "—"}
          </div>
          {primary && <div className="mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">{primary.name}</div>}
        </div>
      </div>
      <div className="min-w-[110px]">
        <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
          {title}
          {accent && (<span className="text-zinc-600">·</span>)}
          {accent && <span className="text-cyan-400 normal-case tracking-normal">{accent}</span>}
        </div>
        {/* Legend list removed — the donut segments + centre label + hover
            tooltip already convey the full mix. Kept the title strip so the
            card stays labelled at a glance. */}
        {data.length === 0 && <div className="text-[10px] text-zinc-600 mt-2">No data in window</div>}
      </div>
    </div>
  );
}
